import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { RunRecord, ScheduleConfig, Tenant, TenantHealth, Zone } from '@irrigation/core';
import { shiftDate, windowWrapsMidnight } from '@irrigation/core';
import { api, type SensorSummary } from '../api.js';
import { Banner, Button, Card, CardSkeleton, EmptyState, Modal, Pill, useToast } from '../components/ui.js';
import {
  DRIFT_FIELD_LABELS,
  errorMessage,
  fmtInches,
  fmtMinutes,
  fmtTime,
  friendlyDate,
  gallons,
  recentDates,
  SKIP_LABELS,
  toIsoDate,
  tomorrowIso,
} from '../lib/format.js';
import { attentionHref, type AttentionTarget } from '../lib/attention.js';
import { appliedInches, isNewSeedSkip, isSeedRun, valveMinutes, weatherSkips } from '../lib/runs.js';
import { currentStatus, type CurrentStatus } from '../lib/status.js';
import { noPlanForOpenWindow } from '../lib/schedule.js';
import { useLoad } from '../lib/useLoad.js';

const DAYS_SHOWN = 7;

/** How often the "watering / soaking" headline recomputes its countdown. */
const TICK_MS = 20_000;
/** How often data is refetched while something is actually in flight. */
const LIVE_POLL_MS = 60_000;
/** Slower refetch when idle, so a page left open notices a run starting. */
const IDLE_POLL_MS = 5 * 60_000;

interface DashboardData {
  tenant: Tenant;
  zones: Zone[];
  config: ScheduleConfig | null;
  runs: RunRecord[];
  health: TenantHealth | null;
  sensors: SensorSummary[];
}

async function loadDashboard(): Promise<DashboardData> {
  const [tenant, zones, config, runs, health, sensors] = await Promise.all([
    api.getMe(),
    api.listZones(),
    api.getConfig().catch(() => null),
    // Include tomorrow: an evening plan for a normal early-morning window is
    // dated to the next day (the session it opens), and a wrap-midnight session
    // still shows under its opening date.
    api.listRuns([tomorrowIso(), ...recentDates(DAYS_SHOWN)]),
    api.getHealth().catch(() => null),
    api.listSensors().catch(() => []),
  ]);
  return { tenant, zones, config, runs, health, sensors };
}

function isPaused(config: ScheduleConfig | null): boolean {
  return Boolean(config?.pausedUntil && config.pausedUntil > new Date().toISOString());
}

export function Dashboard() {
  const { data, loading, error, reload } = useLoad(loadDashboard);

  // A ticking clock so the countdown stays truthful without refetching, plus a
  // slower refetch while water is actually moving (a run can finish, or the
  // next cycle start, without any user action).
  const [now, setNow] = useState(() => Date.now());
  const [stopping, setStopping] = useState(false);
  const toast = useToast();
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(id);
  }, []);

  const status = data
    ? currentStatus({ tenant: data.tenant, zones: data.zones, config: data.config, runs: data.runs, now })
    : null;
  const live = status?.live ?? false;
  useEffect(() => {
    const id = setInterval(
      () => {
        // A background tab has no one to inform — don't spend the API calls.
        if (document.visibilityState === 'visible') void reload();
      },
      live ? LIVE_POLL_MS : IDLE_POLL_MS,
    );
    return () => clearInterval(id);
  }, [live, reload]);

  if (loading) {
    return (
      <div className="stack">
        <CardSkeleton lines={2} />
        <CardSkeleton lines={4} />
      </div>
    );
  }
  if (error || !data) return <Banner tone="error">{error || 'Failed to load.'}</Banner>;

  const { tenant, zones, config, runs, health, sensors } = data;
  const connected = tenant.rachio.status === 'CONNECTED';
  const setupDone = connected && zones.length > 0 && Boolean(tenant.location);
  // Split what's already happened from what's still planned so each gets its own
  // section. A session is KEYED by the date its window opens (a load-bearing
  // decision we don't touch), but for this display we categorize by when it
  // CLOSES: a wrap-midnight window that opens tonight closes tomorrow, so it
  // belongs in "Future", not history. For a same-day window open and close fall
  // on the same date, so this is unchanged.
  const todayIso = toIsoDate(new Date());
  const wraps = config ? windowWrapsMidnight(config.window) : false;
  const sessionCloseDate = (r: RunRecord) => {
    const openDate = r.scheduledAt.slice(0, 10);
    return wraps ? shiftDate(openDate, 1) : openDate;
  };
  const futureRuns = runs.filter((r) => sessionCloseDate(r) > todayIso);
  const recentRuns = runs.filter((r) => sessionCloseDate(r) <= todayIso);

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1>Dashboard</h1>
          <div className="sub">
            {/* Before setup this said "Waters between 4:00 AM and 8:00 AM"
                directly above "Watering starts once the steps below are done" —
                two adjacent lines contradicting each other. */}
            {config && setupDone
              ? `Waters between ${fmtTime(config.window.earliestStart)} and ${fmtTime(config.window.latestEnd)}, when the soil needs it.`
              : 'Weather-based watering, once setup is finished.'}
          </div>
        </div>
        <StatusPills tenant={tenant} zones={zones} config={config} />
      </div>

      {status && (
        <StatusHero
          status={status}
          stopping={stopping}
          onStop={() => {
            setStopping(true);
            void api
              .stopWatering()
              .then(() => {
                toast('Sent stop command to the controller.');
                return reload();
              })
              .catch((e) => toast(errorMessage(e, 'Failed to stop watering.'), 'error'))
              .finally(() => setStopping(false));
          }}
        />
      )}

      {config?.winterized && (
        <Banner tone="info">
          Winterized — planning and watering are suspended until you resume.{' '}
          <WinterResume onChanged={() => void reload()} />
        </Banner>
      )}

      {isPaused(config) && config && (
        <Banner tone="info">
          Watering is paused until {friendlyDate(config.pausedUntil!.slice(0, 10))}. Scheduled runs are skipped until
          then.
        </Banner>
      )}

      <AttentionBanners health={health} tenant={tenant} config={config} runs={runs} zones={zones} />

      {setupDone && <NeedsAttention zones={zones} sensors={sensors} />}

      {!setupDone && <SetupChecklist tenant={tenant} zones={zones} />}

      {setupDone && <Stats runs={runs} zones={zones} />}

      {setupDone && config && !config.winterized && <Controls config={config} onChanged={() => void reload()} />}

      {setupDone && futureRuns.some((r) => !r.unmanaged) && (
        <Card title="Future activity" sub="Watering planned for the upcoming session.">
          <Activity runs={futureRuns} zones={zones} setupDone={setupDone} />
        </Card>
      )}

      <Card
        title="Recent activity"
        sub={`What the scheduler planned and why, over the last ${DAYS_SHOWN} days.`}
      >
        <Activity runs={recentRuns} zones={zones} setupDone={setupDone} />
      </Card>
    </div>
  );
}

/**
 * Per-zone and per-sensor problems, gathered into one list.
 *
 * The banners above cover system-level failures — planner down, controller
 * unreachable — but everything the zones and sensors themselves were raising
 * (settings drift, a calibration suggestion, a probe that stopped reporting, a
 * zone whose sensor never rose after watering) only existed on the Zones and
 * Sensors pages. The dashboard claims to tell you when something needs you, so
 * someone who checked it and left reasonably believed nothing did.
 *
 * Ordered by consequence: things that corrupt watering decisions first, then
 * things that merely make them less accurate.
 */
function NeedsAttention({ zones, sensors }: { zones: Zone[]; sensors: SensorSummary[] }) {
  type Item = { key: string; text: string; target: AttentionTarget; tone: 'bad' | 'warn' };
  const items: Item[] = [];

  // --- decisions are being made on bad data ---
  for (const s of sensors) {
    for (const issue of s.health?.issues ?? []) {
      if (issue.kind === 'STALE') {
        items.push({ key: `${s.id}-stale`, tone: 'bad', target: { page: 'sensors', id: s.id }, text: `${s.name} has stopped reporting — zones using it fall back to the weather model.` });
      } else if (issue.kind === 'FLATLINE') {
        items.push({ key: `${s.id}-flat`, tone: 'bad', target: { page: 'sensors', id: s.id }, text: `${s.name} readings have barely moved in ${issue.hours} h — the probe may be stuck or out of the soil.` });
      }
    }
  }
  for (const z of zones) {
    if (z.sensorResponseAlert) {
      items.push({ key: `${z.id}-resp`, tone: 'bad', target: { page: 'zones', id: z.id }, text: `${z.name} watered but its sensor barely moved — water may not be reaching the zone.` });
    }
    if (z.sensorDisagreementAlert) {
      items.push({
        key: `${z.id}-disagree`,
        tone: 'bad',
        target: { page: 'zones', id: z.id },
        text: `${z.name}'s sensor has read "moist enough" for ${z.sensorDisagreementAlert.days} days while the weather model says it needs water. Its probe may not be in the root zone.`,
      });
    }
    // NO_FLOW belongs up here with the "bad data" group, not with the tuning
    // items: the zone is getting no water at all while the model believes it
    // was watered, so every later decision for it is wrong.
    if (z.flowAnomaly?.kind === 'NO_FLOW') {
      items.push({ key: `${z.id}-noflow`, tone: 'bad', target: { page: 'zones', id: z.id, review: 'flow' }, text: `${z.name} ran but the water meter measured no flow — check its valve, shutoff, or wiring.` });
    }
  }

  // --- accuracy could be better ---
  for (const z of zones) {
    if (z.drift) {
      const fields = z.drift.fields.map((f) => DRIFT_FIELD_LABELS[f.field].toLowerCase()).join(', ');
      items.push({ key: `${z.id}-drift`, tone: 'warn', target: { page: 'zones', id: z.id, review: 'drift' }, text: `${z.name} was changed in the Rachio app (${fields}).` });
    }
    if (z.calibrationSuggestion) {
      items.push({ key: `${z.id}-cal`, tone: 'warn', target: { page: 'zones', id: z.id, review: 'calibration' }, text: `${z.name} has a suggested sensor calibration waiting for review.` });
    }
    if (z.precipRateHint) {
      items.push({ key: `${z.id}-precip`, tone: 'warn', target: { page: 'zones', id: z.id }, text: `${z.name}'s measured water delivery disagrees with its configured rate.` });
    }
    if (z.flowAnomaly && z.flowAnomaly.kind !== 'NO_FLOW') {
      const { kind, observedGalPerMin, baselineGalPerMin } = z.flowAnomaly;
      const direction = kind === 'HIGH_FLOW' ? 'more' : 'less';
      const cause = kind === 'HIGH_FLOW' ? 'a broken head or a leaking line' : 'a clogged nozzle or a partly closed valve';
      items.push({
        key: `${z.id}-flow`,
        tone: 'warn',
        target: { page: 'zones', id: z.id, review: 'flow' },
        text: `${z.name} is using ${direction} water than usual (${observedGalPerMin} gpm vs ${baselineGalPerMin ?? '?'} normally) — often ${cause}.`,
      });
    }
  }
  for (const s of sensors) {
    for (const issue of s.health?.issues ?? []) {
      if (issue.kind === 'LOW_BATTERY') {
        items.push({ key: `${s.id}-batt`, tone: 'warn', target: { page: 'sensors', id: s.id }, text: `${s.name} battery is at ${issue.batteryPct}%.` });
      }
    }
  }

  if (items.length === 0) return null;

  return (
    <Card title={`Needs attention (${items.length})`} sub="Nothing here is stopping watering, but each affects how well it works.">
      <ul className="attention-list">
        {items.map((item) => (
          <li key={item.key}>
            <Pill tone={item.tone} plain>
              {item.tone === 'bad' ? 'Data' : 'Tuning'}
            </Pill>
            <span className="grow">{item.text}</span>
            {/* Every row's link reads "Review", so the label carries the item
                it belongs to for anyone listing the page's links. */}
            <Link className="btn ghost sm" to={attentionHref(item.target)} aria-label={`Review: ${item.text}`}>
              Review
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/**
 * The "what's happening right now" strip at the top of the page — watering,
 * soaking, paused, or idle with what comes next. Announced politely so the
 * headline changing under a screen reader isn't disruptive.
 */
function StatusHero({ status, onStop, stopping }: { status: CurrentStatus; onStop?: () => void; stopping?: boolean }) {
  return (
    <div className={`status-hero ${status.tone}`}>
      <span className="dot" aria-hidden="true" />
      <div className="grow">
        {/* Only the headline is live. The detail carries a countdown that the
            20s tick rewrites, and announcing that on every tick interrupted a
            screen reader three times a minute for the length of a session. */}
        <div className="title" role="status" aria-live="polite">
          {status.title}
        </div>
        <div className="detail">{status.detail}</div>
      </div>
      {/* While water is actually running, Stop belongs here rather than only in
          the Controls card — on a phone that card sits below four stacked stat
          tiles, roughly a screenful down, which is the wrong place for the one
          control you need with wet hands. */}
      {status.live && onStop && (
        <Button variant="danger" busy={stopping} onClick={onStop} className="hero-stop">
          Stop watering
        </Button>
      )}
    </div>
  );
}

/**
 * Plain-language surfacing of scheduler problems that would otherwise only
 * exist in logs: failed planning, a location that went missing after setup,
 * a broken Rachio connection, and days when no plan was made.
 */
function AttentionBanners({
  health,
  tenant,
  config,
  runs,
  zones,
}: {
  health: TenantHealth | null;
  tenant: Tenant;
  config: ScheduleConfig | null;
  runs: RunRecord[];
  zones: Zone[];
}) {
  const banners: ReactNode[] = [];

  if (tenant.rachio.status === 'FAILED') {
    banners.push(
      <Banner tone="error" key="rachio">
        Rachio connection failed{tenant.rachio.error ? ` — ${tenant.rachio.error}` : '.'}{' '}
        <Link to="/connect">Re-connect</Link>
      </Banner>,
    );
  }

  const yesterdayIso = toIsoDate(new Date(Date.now() - 86_400_000));
  const recentFailed = runs.filter((r) => r.status === 'FAILED' && r.scheduledAt.slice(0, 10) >= yesterdayIso);
  if (recentFailed.length > 0) {
    const nameByZone = new Map(zones.map((z) => [z.id, z.name]));
    const names = [...new Set(recentFailed.map((r) => nameByZone.get(r.zoneId) ?? 'a zone'))].join(', ');
    const firstError = recentFailed[0]!.error;
    banners.push(
      <Banner tone="error" key="failed-runs">
        Watering failed for {names}
        {firstError ? ` — ${firstError}` : '.'} Details in Recent activity below.
      </Banner>,
    );
  }

  if (health?.plannerOutcome === 'FAILED') {
    banners.push(
      <Banner tone="error" key="planner">
        The last planning attempt failed{health.plannerError ? ` — ${health.plannerError}` : '.'} The scheduler
        retries hourly; if this persists, check your <Link to="/settings">Settings</Link>.
      </Banner>,
    );
  }

  if (health?.plannerOutcome === 'NO_LOCATION' && !tenant.location) {
    // The setup checklist covers first-time setup; this catches a location
    // that went missing later.
    banners.push(
      <Banner tone="error" key="location">
        Scheduling is stopped: your location is missing, so weather can't be fetched. Set it in{' '}
        <Link to="/settings">Settings</Link>.
      </Banner>,
    );
  }

  // The watering window is open right now but nothing was planned for this
  // session — a genuine gap (e.g. a planner outage), since the scheduler now
  // plans each session proactively before its window opens.
  if (config && noPlanForOpenWindow(config, health, Date.now())) {
    banners.push(
      <Banner tone="info" key="no-plan">
        No plan is in place for the watering window that's open now. If this persists, check your{' '}
        <Link to="/settings">Settings</Link>.
      </Banner>,
    );
  }

  return banners.length > 0 ? <>{banners}</> : null;
}

function StatusPills({
  tenant,
  zones,
  config,
}: {
  tenant: Tenant;
  zones: Zone[];
  config: ScheduleConfig | null;
}) {
  const conn = tenant.rachio;
  return (
    <div className="actions">
      {conn.status === 'CONNECTED' ? (
        <Pill tone="good">{conn.deviceName ?? 'Rachio connected'}</Pill>
      ) : conn.status === 'FAILED' ? (
        <Pill tone="bad">Rachio connection failed</Pill>
      ) : (
        <Pill tone="muted">Rachio not connected</Pill>
      )}
      {config?.winterized ? (
        <Pill tone="muted">Winterized</Pill>
      ) : isPaused(config) ? (
        <Pill tone="warn">Paused</Pill>
      ) : null}
      {zones.length > 0 && (
        <Pill tone="accent">
          {zones.filter((z) => z.enabled).length} of {zones.length} zones active
        </Pill>
      )}
    </div>
  );
}

/** One-click spring resume from the winterized banner. */
function WinterResume({ onChanged }: { onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  return (
    <Button
      variant="subtle"
      size="sm"
      busy={busy}
      onClick={() => {
        setBusy(true);
        api
          .updateConfig({ winterized: false })
          .then(() => {
            toast('Welcome back — planning resumes with the next pass.');
            onChanged();
          })
          .catch(() => toast('Could not resume.', 'error'))
          .finally(() => setBusy(false));
      }}
    >
      Resume now
    </Button>
  );
}

/** Pause / resume / emergency stop. */
function Controls({ config, onChanged }: { config: ScheduleConfig; onChanged: () => void }) {
  const [pausePicker, setPausePicker] = useState(false);
  const [busy, setBusy] = useState('');
  const toast = useToast();

  const act = async (name: string, fn: () => Promise<unknown>, done: string) => {
    setBusy(name);
    try {
      await fn();
      toast(done);
      onChanged();
    } catch (e) {
      toast(errorMessage(e, 'Something went wrong.'), 'error');
    } finally {
      setBusy('');
      setPausePicker(false);
    }
  };

  const pauseFor = (days: number) => {
    const until = new Date(Date.now() + days * 86_400_000).toISOString();
    void act('pause', () => api.updateConfig({ pausedUntil: until }), `Watering paused for ${days} day${days > 1 ? 's' : ''}.`);
  };

  return (
    <Card title="Controls">
      <div className="actions">
        {isPaused(config) ? (
          <Button variant="subtle" busy={busy === 'resume'} onClick={() => void act('resume', () => api.updateConfig({ pausedUntil: null }), 'Watering resumed.')}>
            Resume watering
          </Button>
        ) : (
          <Button variant="ghost" busy={busy === 'pause'} onClick={() => setPausePicker(true)}>
            Pause watering…
          </Button>
        )}
        <Button
          variant="danger"
          busy={busy === 'stop'}
          onClick={() => void act('stop', () => api.stopWatering(), 'Sent stop command to the controller.')}
        >
          Stop all watering now
        </Button>
      </div>

      {pausePicker && (
        <Modal narrow title="Pause watering" sub="Scheduled runs are skipped until the pause lapses." onClose={() => setPausePicker(false)}>
          <div className="actions">
            {[1, 2, 3, 7].map((days) => (
              <Button key={days} variant="ghost" disabled={busy !== ''} onClick={() => pauseFor(days)}>
                {days} day{days > 1 ? 's' : ''}
              </Button>
            ))}
          </div>
        </Modal>
      )}
    </Card>
  );
}

function SetupChecklist({ tenant, zones }: { tenant: Tenant; zones: Zone[] }) {
  const steps = [
    {
      done: tenant.rachio.status === 'CONNECTED',
      title: 'Connect your Rachio controller',
      desc: 'Paste your Rachio API token so the scheduler can command your valves.',
      to: '/connect',
      cta: 'Connect',
    },
    {
      done: zones.length > 0,
      title: 'Import your zones',
      desc: 'Pull zones from Rachio, then tune plant, soil, and nozzle details.',
      to: '/connect',
      cta: 'Import',
    },
    {
      done: Boolean(tenant.location),
      title: 'Set your location',
      desc: 'Weather and evapotranspiration are fetched for your coordinates.',
      to: '/settings',
      cta: 'Set location',
    },
  ];
  const remaining = steps.filter((s) => !s.done).length;

  return (
    <Card
      title="Finish setting up"
      sub={`${steps.length - remaining} of ${steps.length} done — the planner starts once everything is in place.`}
    >
      <div className="steps">
        {steps.map((s, i) => (
          <div key={s.title} className={`step${s.done ? ' done' : ''}`}>
            <span className="bullet" aria-hidden="true">
              {s.done ? '✓' : i + 1}
            </span>
            <div className="grow">
              <div className="title">{s.title}</div>
              <div className="desc">{s.desc}</div>
            </div>
            {!s.done && (
              <Link className="btn subtle sm" to={s.to}>
                {s.cta}
              </Link>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}

function Stats({ runs, zones }: { runs: RunRecord[]; zones: Zone[] }) {
  // Only real water we can account for counts toward usage: dry-runs applied
  // nothing, and unmanaged runs are zones we don't track (no zone means no
  // measurable depth).
  const watered = runs.filter((r) => r.status === 'COMPLETED' && !r.dryRun && !r.unmanaged);
  const dryRuns = runs.filter((r) => r.status === 'COMPLETED' && r.dryRun);
  // The new-seed hand-off is not a weather decision, so it doesn't belong in a
  // count of skips the forecast earned.
  const skipped = weatherSkips(runs);
  const inches = watered.reduce((sum, r) => sum + appliedInches(r), 0);
  const minutes = watered.reduce((sum, r) => sum + valveMinutes(r), 0);

  // Gallons: metered where a water meter attributed them, estimated from zone
  // area otherwise. Both are approximate — a metered figure is the zone's
  // per-minute rate times its valve time, from a whole-house meter — so every
  // form here carries the ≈, and the detail line says which source dominates.
  const areaByZone = new Map(zones.map((z) => [z.id, z.areaSqFt]));
  const measuredGal = watered.reduce(
    (sum, r) => (r.measuredConfidence === 'CLEAN' ? sum + (r.measuredGal ?? 0) : sum),
    0,
  );
  const measuredRuns = watered.filter((r) => r.measuredConfidence === 'CLEAN').length;
  const estimatedGal = watered.reduce((sum, r) => {
    if (r.measuredConfidence === 'CLEAN') return sum;
    const area = areaByZone.get(r.zoneId);
    return area ? sum + gallons(appliedInches(r), area) : sum;
  }, 0);
  const gal = measuredGal + estimatedGal;
  const gallonsDetail =
    gal <= 0
      ? 'gross depth across zones'
      : measuredRuns === 0
        ? `≈ ${Math.round(gal).toLocaleString()} gal (estimated from zone area)`
        : measuredRuns === watered.length
          ? `≈ ${Math.round(gal).toLocaleString()} gal from meter`
          : `≈ ${Math.round(gal).toLocaleString()} gal (${measuredRuns} of ${watered.length} runs metered)`;

  return (
    <div className="grid cols-4 stats">
      <div className="stat">
        <div className="label">Waterings · {DAYS_SHOWN}d</div>
        <div className="value">
          {watered.length}
          {dryRuns.length > 0 && <small>+{dryRuns.length} dry-run</small>}
        </div>
        <div className="detail">{minutes > 0 ? `${fmtMinutes(minutes)} total runtime` : 'no valve time yet'}</div>
      </div>
      <div className="stat">
        <div className="label">Water applied</div>
        <div className="value">
          {inches.toFixed(inches >= 10 ? 1 : 2)}
          <small>in</small>
        </div>
        <div className="detail">{gallonsDetail}</div>
      </div>
      <div className="stat">
        <div className="label">Smart skips</div>
        <div className="value">{skipped.length}</div>
        <div className="detail">rain, freeze, wind, day rules</div>
      </div>
      <div className="stat">
        <div className="label">Active zones</div>
        <div className="value">{zones.filter((z) => z.enabled).length}</div>
        <div className="detail">of {zones.length} configured</div>
      </div>
    </div>
  );
}

function Activity({ runs, zones, setupDone }: { runs: RunRecord[]; zones: Zone[]; setupDone: boolean }) {
  // Unmanaged runs (a zone we don't track that the controller ran) only exist
  // to light up the live status strip — they carry no water and belong to no
  // zone, so they don't appear in the history list.
  runs = runs.filter((r) => !r.unmanaged);
  if (runs.length === 0) {
    return (
      <EmptyState icon="🌤" title="No activity yet">
        {setupDone
          ? 'The planner checks the forecast every hour — your first plan should appear by this evening, and waters in the next morning window if the soil needs it.'
          : 'Finish setup above and the planner will take it from there.'}
      </EmptyState>
    );
  }

  const nameByZone = new Map(zones.map((z) => [z.id, z.name]));
  const byDate = new Map<string, RunRecord[]>();
  for (const run of runs) {
    const date = run.scheduledAt.slice(0, 10);
    const group = byDate.get(date);
    if (group) group.push(run);
    else byDate.set(date, [run]);
  }
  const dates = [...byDate.keys()].sort().reverse();

  return (
    <div className="list">
      {dates.map((date, i) => {
        const dayRuns = byDate.get(date)!.sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt));
        const rows = dayRuns.map((r) => (
          <RunRow key={r.id} run={r} zoneName={nameByZone.get(r.zoneId) ?? 'Removed zone'} />
        ));

        // The most recent day, and any day something went wrong, stay open. The
        // rest collapse: a healthy week is ~40 "skipped, as designed" rows and
        // two real waterings, and at full resolution the two that matter are
        // invisible — so people stop reading, and then miss a failure.
        const failed = dayRuns.some((r) => r.status === 'FAILED');
        if (i === 0 || failed) {
          return (
            <div key={date}>
              <div className="run-day">{friendlyDate(date)}</div>
              <div className="list" role="list">{rows}</div>
            </div>
          );
        }

        return (
          <details key={date} className="run-day-group">
            <summary>
              <span className="run-day">{friendlyDate(date)}</span>
              <span className="day-summary">{summarizeDay(dayRuns, nameByZone)}</span>
            </summary>
            <div className="list" role="list">{rows}</div>
          </details>
        );
      })}
    </div>
  );
}

/**
 * One line standing in for a day's runs: what actually watered, then why the
 * rest didn't, counted by reason rather than repeated per zone.
 */
function summarizeDay(runs: RunRecord[], nameByZone: Map<string, string>): string {
  const watered = runs.filter((r) => r.status === 'COMPLETED' && r.plan.shouldWater);
  const parts: string[] = [];

  if (watered.length > 0) {
    const names = watered.map((r) => nameByZone.get(r.zoneId) ?? 'Removed zone');
    const total = watered.reduce((sum, r) => sum + appliedInches(r), 0);
    parts.push(`${names.join(', ')} watered ${fmtInches(total)}`);
  }

  // The NEW_SEED session run is a hand-off to the misting scheduler, not a
  // skip: the zone WAS watered that day, and its misting record is in this
  // same group saying so.
  const others = runs.filter((r) => !watered.includes(r) && !isNewSeedSkip(r));
  if (others.length > 0) {
    const byReason = new Map<string, number>();
    for (const r of others) {
      // Plans carry zero or more skip reasons; no reason means the soil simply
      // hadn't dried out enough to trigger.
      const reason = r.plan.skips.map((s) => SKIP_LABELS[s] ?? String(s).toLowerCase()).join(' + ') || 'soil still moist';
      byReason.set(reason, (byReason.get(reason) ?? 0) + 1);
    }
    const detail = [...byReason.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([reason, n]) => (n > 1 ? `${reason} ×${n}` : reason))
      .join(', ');
    parts.push(`${others.length} skipped (${detail})`);
  }

  return parts.join(' · ') || 'nothing scheduled';
}

function RunRow({ run, zoneName }: { run: RunRecord; zoneName: string }) {
  const watered = run.status === 'COMPLETED';
  // A new-seed record holds the whole day's misting program, and only some of
  // its cycles may have fired (an outage expires the stale ones), so both
  // figures come off what really ran rather than the plan.
  const seed = isSeedRun(run);
  const shownMinutes = valveMinutes(run);
  return (
    <div className="run-row" role="listitem">
      <div className="grow">
        <div className="title">
          {zoneName}
          <RunStatusPill run={run} />
          {run.id.startsWith('manual-') && (
            <Pill tone="muted" plain>
              Manual
            </Pill>
          )}
          {run.id.startsWith('external-') && (
            <Pill tone="muted" plain>
              Outside app
            </Pill>
          )}
          {seed && (
            <Pill tone="muted" plain>
              New seed
            </Pill>
          )}
        </div>
        <div className="meta">{run.plan.explanation}</div>
      </div>
      {watered && shownMinutes > 0 && (
        <div className="figures">
          <b>{fmtInches(appliedInches(run))}</b>
          <br />
          {fmtMinutes(shownMinutes)}
          {run.measuredConfidence === 'CLEAN' && run.measuredGalPerMin !== undefined && (
            <> · {run.measuredGalPerMin.toFixed(1)} gpm</>
          )}
        </div>
      )}
    </div>
  );
}

function RunStatusPill({ run }: { run: RunRecord }) {
  switch (run.status) {
    case 'SKIPPED': {
      const reasons = run.plan.skips.map((s) => SKIP_LABELS[s] ?? s.toLowerCase()).join(', ');
      return <Pill tone="warn">Skipped{reasons ? ` — ${reasons}` : ''}</Pill>;
    }
    case 'FAILED':
      return <Pill tone="bad">Failed</Pill>;
    case 'COMPLETED':
      return run.dryRun ? <Pill tone="accent">Dry-run</Pill> : <Pill tone="good">Watered</Pill>;
    case 'RUNNING':
      return <Pill tone="accent">Running</Pill>;
    default:
      return <Pill tone="muted">Planned</Pill>;
  }
}
